import { useEffect, useId } from 'react';

export const OPEN_SHORTCUTS_EVENT = 'craftara:open-shortcuts';

export function requestOpenShortcuts(): void {
  window.dispatchEvent(new CustomEvent(OPEN_SHORTCUTS_EVENT));
}

interface ShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

const GROUPS: Array<{ title: string; rows: Array<[string, string]> }> = [
  {
    title: 'Tools',
    rows: [
      ['V', 'Move / Select'],
      ['M', 'Marquee · again = ellipse (Shift = add)'],
      ['L', 'Lasso object select (Shift = add)'],
      ['B', 'Brush'],
      ['N', 'Pencil'],
      ['E', 'Eraser'],
      ['P', 'Pen'],
      ['U', 'Cycle shapes'],
      ['T', 'Cycle type'],
      ['F', 'Frame'],
      ['G', 'Gradient'],
      ['K', 'Paint bucket'],
      ['I', 'Eyedropper'],
      ['C', 'Crop'],
      ['H', 'Hand'],
      ['Z', 'Zoom (Alt = out)'],
    ],
  },
  {
    title: 'Edit & view',
    rows: [
      ['Ctrl+Z', 'Undo'],
      ['Ctrl+Y', 'Redo'],
      ['Ctrl+C / V / D', 'Copy / Paste / Duplicate'],
      ['Ctrl+A', 'Select all'],
      ['Arrow / Shift+Arrow', 'Nudge 1px / 10px'],
      ['0–9', 'Opacity (0=100%, 1=10%…; two digits quick)'],
      ['Ctrl+] / [', 'Bring forward / Send backward'],
      ['Ctrl+Shift+] / [', 'Bring to front / Send to back'],
      ['Ctrl+S', 'Save'],
      ['Ctrl+E', 'Export'],
      ['Ctrl+G', 'Group'],
      ['Ctrl+Shift+G', 'Ungroup'],
      ['Ctrl+R', 'Toggle rulers'],
      ['Ctrl+\\', 'Toggle libraries panel'],
      ['Ctrl+Shift+\\', 'Toggle inspector panel'],
      ['Ctrl+0', 'Zoom 100%'],
      ['F5 / Ctrl+Enter', 'Present'],
      ['Space', 'Temporary pan (play / pause in motion mode)'],
      ['Delete', 'Delete selection'],
      ['? or Ctrl+/', 'This shortcuts list'],
    ],
  },
  {
    title: 'Properties',
    rows: [
      ['Fill / Stroke', 'Color + stroke width'],
      ['Font', 'Family, size, bold, italic, align'],
      ['Corner radius', 'Rectangles'],
      ['Drop shadow', 'Enable + blur / offset'],
      ['Blend mode', 'Normal, Multiply, Screen…'],
      ['Pathfinder', 'Union / Subtract / Intersect / Exclude'],
      ['Type on path', 'Select path/shape → Properties'],
      ['Flip H / V', 'Mirror selection'],
      ['±90°', 'Rotate selection'],
      ['Artboard size', 'Click W×H in the menubar'],
      ['Ruler guides', 'Drag from rulers · Lock guides in status'],
      ['Grid / Snap', 'Status bar toggles'],
      ['Grayscale', 'Image adjustments checkbox'],
    ],
  },
  {
    title: 'Motion mode',
    rows: [
      ['Space', 'Play / pause the open scene'],
      [', / .', 'Step one frame back / forward'],
      ['J / K / L', 'Step back / pause / play'],
      ['[ / ]', 'Trim in / out (scene length with no clip selected)'],
      ['Shift + [ / ]', 'Extend instead of trim'],
    ],
  },
];

export default function ShortcutsHelp({ open, onClose }: ShortcutsHelpProps) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="ps-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="ps-modal ps-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ps-modal-header">
          <h2 id={titleId}>Keyboard shortcuts</h2>
          <button type="button" className="ps-menu-link" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="ps-modal-body ps-shortcuts-grid">
          {GROUPS.map((group) => (
            <section key={group.title}>
              <h3>{group.title}</h3>
              <table>
                <tbody>
                  {group.rows.map(([keys, label]) => (
                    <tr key={keys}>
                      <td>
                        <kbd>{keys}</kbd>
                      </td>
                      <td>{label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
